import { useState, useMemo } from 'react'
import {
  Box,
  TextField,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  InputAdornment,
} from '@mui/material'
import { Search as SearchIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { FunctionApp } from '../types/FunctionApp'

interface FunctionAppListPanelProps {
  apps: FunctionApp[]
  selectedApp: FunctionApp | null
  onSelect: (app: FunctionApp) => void
}

const FunctionAppListPanel = ({ apps, selectedApp, onSelect }: FunctionAppListPanelProps) => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState('')

  const groupedApps = useMemo(() => {
    const filtered = apps.filter((a) =>
      a.name.toLowerCase().includes(filter.toLowerCase()),
    )
    const groups: Record<string, FunctionApp[]> = {}
    for (const app of filtered) {
      const key = app.subscriptionName
      if (!groups[key]) groups[key] = []
      groups[key].push(app)
    }
    return groups
  }, [apps, filter])

  return (
    <Box
      sx={{
        width: 280,
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          fullWidth
          placeholder={t('command.filterApps', 'Filter apps...')}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      <List sx={{ flex: 1, overflow: 'auto', py: 0 }} dense>
        {Object.entries(groupedApps).map(([subscription, subApps]) => (
          <li key={subscription}>
            <ul style={{ padding: 0 }}>
              <ListSubheader sx={{ lineHeight: '32px', fontSize: '0.7rem' }}>
                {subscription}
              </ListSubheader>
              {subApps.map((app) => (
                <ListItemButton
                  key={app.id}
                  selected={selectedApp?.id === app.id}
                  onClick={() => onSelect(app)}
                >
                  <ListItemText
                    primary={app.name}
                    slotProps={{ primary: { fontSize: '0.85rem' } }}
                  />
                </ListItemButton>
              ))}
            </ul>
          </li>
        ))}
      </List>
    </Box>
  )
}

export default FunctionAppListPanel
